import {ModuleComponent} from "../../js/module-component";

const VERTEX = `
attribute vec2 position;
varying vec2 vUv;
void main(){
    vUv = position * 0.5 + 0.5;
    gl_Position = vec4(position, 0.0, 1.0);
}`;

// Velocidad y fuerza del cursor codificadas en RGBA8 (0.5 = cero) para no depender de texturas float.
const FLOW_FRAGMENT = `
precision highp float;
uniform sampler2D tMap;
uniform vec2 uMouse;
uniform vec2 uVelocity;
uniform float uAspect;
uniform float uFalloff;
uniform float uDissipation;
varying vec2 vUv;
void main(){
    vec3 f = texture2D(tMap, vUv).rgb * 2.0 - 1.0;
    f *= uDissipation;
    f = sign(f) * max(abs(f) - 0.004, 0.0);
    vec2 cursor = vUv - uMouse;
    cursor.x *= uAspect;
    vec3 stamp = vec3(uVelocity * vec2(1.0, -1.0), 1.0 - pow(1.0 - min(1.0, length(uVelocity)), 3.0));
    float falloff = smoothstep(uFalloff, 0.0, length(cursor));
    f = mix(f, stamp, falloff);
    gl_FragColor = vec4(f * 0.5 + 0.5, 1.0);
}`;

const DISPLAY_FRAGMENT = `
precision highp float;
uniform sampler2D tImage;
uniform sampler2D tFlow;
uniform vec2 uCover;
varying vec2 vUv;
void main(){
    vec3 flow = texture2D(tFlow, vUv).rgb * 2.0 - 1.0;
    vec2 uv = (vUv - 0.5) * uCover + 0.5;
    vec2 shift = flow.xy * 0.11;
    float r = texture2D(tImage, uv - shift * 1.08).r;
    vec2 gb = texture2D(tImage, uv - shift).gb;
    gl_FragColor = vec4(r, gb, 1.0);
}`;

const FLOW_SIZE = 128;

/**
 * Hero Ganbaru tipo 1: titular con degradado animado letra a letra y fondo que se deforma como un líquido al paso del cursor.
 */
export class ModHeroLiquid extends ModuleComponent{

    ready(){
        super.ready();
        this.el = this.$root.get(0);
        if(!this.el) return;

        this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        this.onResize = () => this.resize();
        window.addEventListener("resize", this.onResize);

        this.splitTitle();
        this.loadBackground();
    }

    /**
     * Parte el titular en palabras y letras para animarlas manteniendo el texto accesible en aria-label.
     */
    splitTitle(){
        this.title = this.findMember("title").get(0);
        if(!this.title || this.reduceMotion) return;

        const text = this.title.textContent.trim().replace(/\s+/g, " ");
        const words = text.split(" ");
        let index = 0;

        this.title.setAttribute("aria-label", text);
        this.title.textContent = "";
        words.forEach((w, wi) => {
            const word = document.createElement("span");
            word.className = "word";
            word.setAttribute("aria-hidden", "true");
            Array.from(w).forEach(ch => {
                const letter = document.createElement("span");
                letter.className = "letter";
                letter.textContent = ch;
                letter.style.animationDelay = (0.25 + index * 0.022).toFixed(3) + "s";
                word.appendChild(letter);
                index++;
            });
            this.title.appendChild(word);
            if(wi < words.length - 1){
                this.title.appendChild(document.createTextNode(" "));
            }
        });
        this.title.classList.add("is-split");
        this.letters = Array.from(this.title.querySelectorAll(".letter"));
        this.paintTitleGradient();
        if(document.fonts && document.fonts.ready){
            document.fonts.ready.then(() => this.paintTitleGradient());
        }
    }

    /**
     * Ajusta el degradado de cada letra al tamaño del titular para que se vea continuo.
     */
    paintTitleGradient(){
        if(!this.letters) return;
        const w = this.title.offsetWidth;
        const h = this.title.offsetHeight;
        this.letters.forEach(l => {
            l.style.backgroundSize = `${w}px ${h}px`;
            l.style.backgroundPosition = `${-l.offsetLeft}px ${-l.offsetTop}px`;
        });
    }

    loadBackground(){
        const url = this.$root.data("bg");
        if(!url){
            const texture = this.createBrandTexture();
            this.findMember("bg").css("background-image", `url(${texture.toDataURL("image/jpeg", 0.9)})`);
            this.initFlowmap(texture);
            return;
        }
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => this.initFlowmap(img);
        img.src = url;
    }

    /**
     * Fondo de marca generado cuando el CMS no aporta imagen: brillos verde/azul y un campo de celdas en diagonal.
     * @returns {HTMLCanvasElement}
     */
    createBrandTexture(){
        const w = 2880, h = 2088;
        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        const ctx = c.getContext("2d");

        ctx.fillStyle = "#081221";
        ctx.fillRect(0, 0, w, h);

        const glow = (x, y, r, color) => {
            const g = ctx.createRadialGradient(x, y, 0, x, y, r);
            g.addColorStop(0, color);
            g.addColorStop(1, "rgba(8,18,33,0)");
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, w, h);
        };
        glow(w * .68, h * .40, w * .55, "rgba(0,178,137,.6)");
        glow(w * .95, h * .85, w * .48, "rgba(22,133,170,.65)");
        glow(w * .45, h * 1.05, w * .30, "rgba(0,178,137,.25)");

        // Banda diagonal de celdas: al deformarse con el flowmap da la sensación de tejido vivo.
        const step = 40;
        const lineA = {x: w * .30, y: h * 1.15};
        const lineB = {x: w * 1.00, y: -h * .15};
        const dx = lineB.x - lineA.x, dy = lineB.y - lineA.y;
        const len = Math.hypot(dx, dy);
        const band = w * .30;
        for(let y = 0; y < h + step; y += step * .87){
            const row = Math.round(y / (step * .87));
            for(let x = (row % 2) * step / 2; x < w + step; x += step){
                const dist = Math.abs(dy * (x - lineA.x) - dx * (y - lineA.y)) / len;
                if(dist > band) continue;
                const k = 1 - dist / band;
                const t = Math.min(1, Math.max(0, ((x - lineA.x) * dx + (y - lineA.y) * dy) / (len * len)));
                const r = Math.round(0 + t * 22), g = Math.round(178 - t * 45), b = Math.round(137 + t * 33);
                const radius = step * (.16 + .24 * k) * (0.85 + Math.random() * .3);
                ctx.beginPath();
                ctx.arc(x, y, radius, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${r},${g},${b},${(.12 + .7 * k * k).toFixed(3)})`;
                ctx.fill();
            }
        }

        // Filo de luz a lo largo de la banda.
        ctx.save();
        ctx.filter = "blur(28px)";
        ctx.strokeStyle = "rgba(159,245,223,.35)";
        ctx.lineWidth = 20;
        ctx.beginPath();
        ctx.moveTo(lineA.x + band * .9, lineA.y);
        ctx.lineTo(lineB.x + band * .9, lineB.y);
        ctx.stroke();
        ctx.restore();

        return c;
    }

    /**
     * Arranca el render WebGL: un búfer de flujo que acumula el movimiento del cursor y un pase que desplaza la imagen con él.
     * @param {HTMLImageElement|HTMLCanvasElement} source
     */
    initFlowmap(source){
        if(this.reduceMotion) return;
        this.canvas = this.findMember("canvas").get(0);
        const gl = this.canvas && this.canvas.getContext("webgl", {antialias: false, alpha: false, preserveDrawingBuffer: false});
        if(!gl) return;
        this.gl = gl;
        this.imageAspect = (source.naturalWidth || source.width) / (source.naturalHeight || source.height);

        this.flowProgram = this.createProgram(VERTEX, FLOW_FRAGMENT);
        this.displayProgram = this.createProgram(VERTEX, DISPLAY_FRAGMENT);
        if(!this.flowProgram || !this.displayProgram) return;

        this.buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);

        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
        this.imageTexture = this.createTexture();
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);

        this.targets = [this.createTarget(), this.createTarget()];

        this.mouse = [-1, -1];
        this.velocity = [0, 0];
        this.velocityTarget = [0, 0];
        this.moved = false;
        this.onPointerMove = e => this.pointerMove(e);
        this.el.addEventListener("pointermove", this.onPointerMove);

        this.visible = true;
        if("IntersectionObserver" in window){
            this.observer = new IntersectionObserver(entries => {
                this.visible = entries[0].isIntersecting;
                if(this.visible) this.loop();
            });
            this.observer.observe(this.el);
        }

        this.resize();
        this.$root.addClass("is-webgl");
        this.loop();
    }

    createProgram(vs, fs){
        const gl = this.gl;
        const compile = (type, src) => {
            const s = gl.createShader(type);
            gl.shaderSource(s, src);
            gl.compileShader(s);
            return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
        };
        const v = compile(gl.VERTEX_SHADER, vs);
        const f = compile(gl.FRAGMENT_SHADER, fs);
        if(!v || !f) return null;
        const p = gl.createProgram();
        gl.attachShader(p, v);
        gl.attachShader(p, f);
        gl.linkProgram(p);
        if(!gl.getProgramParameter(p, gl.LINK_STATUS)) return null;
        p.uniforms = {};
        const n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
        for(let i = 0; i < n; i++){
            const name = gl.getActiveUniform(p, i).name;
            p.uniforms[name] = gl.getUniformLocation(p, name);
        }
        p.position = gl.getAttribLocation(p, "position");
        return p;
    }

    createTexture(){
        const gl = this.gl;
        const t = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, t);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        return t;
    }

    createTarget(){
        const gl = this.gl;
        const texture = this.createTexture();
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, FLOW_SIZE, FLOW_SIZE, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        const fbo = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
        gl.clearColor(0.5, 0.5, 0.5, 1);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        return {texture, fbo};
    }

    pointerMove(e){
        const rect = this.el.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const now = performance.now();
        this.mouse = [x / rect.width, 1 - y / rect.height];
        if(this.lastPointer){
            const dt = Math.max(14, now - this.lastPointer.t);
            this.velocityTarget = [(x - this.lastPointer.x) / dt, (y - this.lastPointer.y) / dt];
        }
        this.lastPointer = {x, y, t: now};
        this.moved = true;
    }

    resize(){
        this.paintTitleGradient();
        if(!this.gl) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const w = Math.max(1, Math.round(this.el.clientWidth * dpr));
        const h = Math.max(1, Math.round(this.el.clientHeight * dpr));
        this.canvas.width = w;
        this.canvas.height = h;
        this.aspect = w / h;
        this.cover = this.aspect > this.imageAspect ? [1, this.imageAspect / this.aspect] : [this.aspect / this.imageAspect, 1];
    }

    loop(){
        if(this.raf || !this.gl) return;
        const tick = () => {
            this.raf = null;
            if(!this.visible || !this.gl) return;
            this.render();
            this.raf = requestAnimationFrame(tick);
        };
        this.raf = requestAnimationFrame(tick);
    }

    draw(program){
        const gl = this.gl;
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
        gl.enableVertexAttribArray(program.position);
        gl.vertexAttribPointer(program.position, 2, gl.FLOAT, false, 0, 0);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    render(){
        const gl = this.gl;
        if(!this.moved){
            this.mouse = [-1, -1];
            this.velocityTarget = [0, 0];
        }
        this.moved = false;
        const ease = this.velocityTarget[0] || this.velocityTarget[1] ? 0.5 : 0.1;
        this.velocity[0] += (this.velocityTarget[0] - this.velocity[0]) * ease;
        this.velocity[1] += (this.velocityTarget[1] - this.velocity[1]) * ease;

        const [read, write] = this.targets;
        const fp = this.flowProgram;
        gl.useProgram(fp);
        gl.bindFramebuffer(gl.FRAMEBUFFER, write.fbo);
        gl.viewport(0, 0, FLOW_SIZE, FLOW_SIZE);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, read.texture);
        gl.uniform1i(fp.uniforms.tMap, 0);
        gl.uniform2f(fp.uniforms.uMouse, this.mouse[0], this.mouse[1]);
        gl.uniform2f(fp.uniforms.uVelocity, this.velocity[0], this.velocity[1]);
        gl.uniform1f(fp.uniforms.uAspect, this.aspect);
        gl.uniform1f(fp.uniforms.uFalloff, 0.22);
        gl.uniform1f(fp.uniforms.uDissipation, 0.965);
        this.draw(fp);
        this.targets = [write, read];

        const dp = this.displayProgram;
        gl.useProgram(dp);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.imageTexture);
        gl.uniform1i(dp.uniforms.tImage, 0);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, write.texture);
        gl.uniform1i(dp.uniforms.tFlow, 1);
        gl.uniform2f(dp.uniforms.uCover, this.cover[0], this.cover[1]);
        this.draw(dp);
    }

    destroy(){
        window.removeEventListener("resize", this.onResize);
        if(this.onPointerMove) this.el.removeEventListener("pointermove", this.onPointerMove);
        if(this.observer) this.observer.disconnect();
        if(this.raf) cancelAnimationFrame(this.raf);
        this.gl = null;
        if(super.destroy) super.destroy();
    }
}
